'use client'

import { Suspense, useState, useEffect } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { createClient } from '@/lib/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from 'sonner'
import { getSafeRedirect } from '@/lib/auth/validate-redirect'

function LoginForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [magicLinkCooldown, setMagicLinkCooldown] = useState(0)
  const router = useRouter()
  const searchParams = useSearchParams()
  // C2 FIX: Validate redirect parameter to prevent open redirect attacks
  const redirect = getSafeRedirect(searchParams.get('redirect'))
  const supabase = createClient()

  // M2 FIX: Cooldown timer for magic link rate limiting
  useEffect(() => {
    if (magicLinkCooldown > 0) {
      const timer = setTimeout(() => setMagicLinkCooldown(c => c - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [magicLinkCooldown])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      // M3 FIX: Generic error message to prevent user enumeration
      toast.error('Invalid email or password')
      setLoading(false)
      return
    }

    toast.success('Logged in successfully')
    router.push(redirect)
    router.refresh()
  }

  const handleMagicLink = async () => {
    if (!email) {
      toast.error('Please enter your email')
      return
    }

    // M2 FIX: Check cooldown before sending magic link
    if (magicLinkCooldown > 0) {
      toast.error(`Please wait ${magicLinkCooldown} seconds before requesting another link`)
      return
    }

    setLoading(true)

    // C1 FIX: Use NEXT_PUBLIC_SITE_URL for production redirect
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${siteUrl}/auth/callback?redirect=${encodeURIComponent(redirect)}`,
      },
    })

    if (error) {
      // M3 FIX: Always show success message to prevent user enumeration
      // The user might not exist, but we don't want to reveal that
      console.error('Magic link error:', error.message)
    }

    // M2 FIX: Start 60-second cooldown
    setMagicLinkCooldown(60)

    toast.success('Check your email for the magic link')
    setLoading(false)
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
        <CardDescription>
          Sign in to your C4 Owner account
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleLogin}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleMagicLink}
            disabled={loading || magicLinkCooldown > 0}
          >
            {magicLinkCooldown > 0
              ? `Send magic link (${magicLinkCooldown}s)`
              : 'Send magic link'}
          </Button>
          <p className="text-sm text-center text-muted-foreground">
            Don&apos;t have an account?{' '}
            <Link href="/signup" className="text-primary hover:underline">
              Sign up
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  )
}

export default function LoginPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Suspense fallback={
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
            <CardDescription>Loading...</CardDescription>
          </CardHeader>
        </Card>
      }>
        <LoginForm />
      </Suspense>
    </div>
  )
}
